/**
 * ConfirmSageDialog / UndoConfirmSageDialog — the two confirm steps around the
 * manual "Confirm in Sage" action (kanban c79be365).
 *
 * "Confirm in Sage" used to fire on one click with no check that the order
 * existed in Sage, and there was no way back. Now:
 *  - when the order is already OPEN in Sage the dialog just asks the CSR to
 *    confirm the AU# it is linked to;
 *  - when it is NOT, the dialog warns and requires the AU# the order was keyed
 *    under (the server refuses a number that is not an open Sage SO for the
 *    same customer, and links it on success);
 *  - "Undo confirm" asks before moving a mistakenly-confirmed order back to
 *    the retry lane (Submission Failed), where ship via etc. are editable.
 */
import { useState } from 'react';
import styles from './OrderDetailModal.module.css';

interface ConfirmSageDialogProps {
  /** The AU# the order is linked to, if any. */
  sageOrderNo: string | null;
  /** Server-side fact: the order's AU# is a present, non-deleted open SO. */
  openInSage: boolean;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  /** `sageOrderNo` is the AU# to link (trimmed), or null when already open. */
  onConfirm: (sageOrderNo: string | null) => void;
}

export function ConfirmSageDialog({
  sageOrderNo,
  openInSage,
  submitting,
  error,
  onCancel,
  onConfirm,
}: ConfirmSageDialogProps) {
  const [entered, setEntered] = useState('');
  const trimmed = entered.trim();
  const canConfirm = !submitting && (openInSage || trimmed.length > 0);

  return (
    <div className={styles.confirmOverlay} onClick={onCancel}>
      <div
        className={styles.confirmModal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="confirm-sage-title"
        data-testid="confirm-sage-dialog"
      >
        <div className={styles.confirmTitle} id="confirm-sage-title">Confirm in Sage</div>
        {openInSage ? (
          <p className={styles.confirmText}>
            Sage order <strong>{sageOrderNo}</strong> is open in Sage. Mark this order as
            confirmed there?
          </p>
        ) : (
          <>
            <p className={styles.confirmText} data-testid="confirm-sage-not-in-sage">
              <strong>This order is not in Sage.</strong> Nothing was found under
              {sageOrderNo ? ` ${sageOrderNo}` : ' any Sage order number'}. Confirming
              without a Sage order only locks the order here; it does not create it in Sage.
              If it was keyed by hand, enter the Sage order number (AU#) it was keyed under.
              Otherwise close this and use Retry Submit to Sage.
            </p>
            <label className={styles.confirmText} htmlFor="confirm-sage-order-no">
              Sage order number
            </label>
            <input
              id="confirm-sage-order-no"
              data-testid="confirm-sage-order-no"
              type="text"
              placeholder="AU01234"
              value={entered}
              onChange={(e) => setEntered(e.target.value)}
              autoFocus
              disabled={submitting}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 12 }}
            />
          </>
        )}
        {error && (
          <p role="alert" data-testid="confirm-sage-error" style={{ color: '#b91c1c', fontSize: 13, marginTop: 0 }}>
            {error}
          </p>
        )}
        <div className={styles.confirmActions}>
          <button
            className={`${styles.actionBtn} ${styles.actionCancel}`}
            onClick={onCancel}
            disabled={submitting}
            type="button"
          >
            Back
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionConfirmSage}`}
            data-testid="confirm-sage-submit"
            onClick={() => onConfirm(openInSage ? null : trimmed)}
            disabled={!canConfirm}
            type="button"
          >
            {submitting ? 'Confirming…' : openInSage ? 'Confirm in Sage' : 'Link and confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface UndoConfirmSageDialogProps {
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function UndoConfirmSageDialog({ submitting, error, onCancel, onConfirm }: UndoConfirmSageDialogProps) {
  return (
    <div className={styles.confirmOverlay} onClick={onCancel}>
      <div
        className={styles.confirmModal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="undo-confirm-sage-title"
        data-testid="undo-confirm-sage-dialog"
      >
        <div className={styles.confirmTitle} id="undo-confirm-sage-title">Undo confirm</div>
        <p className={styles.confirmText}>
          This order was marked confirmed by hand but is not in Sage. Undoing moves it back to
          Submission Failed so you can fix the order (ship via, lines, ship-to) and Retry Submit
          to Sage. Nothing is sent to Sage.
        </p>
        {error && (
          <p role="alert" data-testid="undo-confirm-sage-error" style={{ color: '#b91c1c', fontSize: 13, marginTop: 0 }}>
            {error}
          </p>
        )}
        <div className={styles.confirmActions}>
          <button
            className={`${styles.actionBtn} ${styles.actionCancel}`}
            onClick={onCancel}
            disabled={submitting}
            type="button"
          >
            Keep confirmed
          </button>
          <button
            className={styles.actionBtn}
            data-testid="undo-confirm-sage-submit"
            style={{ background: '#b45309', color: '#fff', border: '1px solid #b45309', fontWeight: 600 }}
            onClick={onConfirm}
            disabled={submitting}
            type="button"
          >
            {submitting ? 'Undoing…' : 'Undo confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
